import { useState, useCallback, useEffect, useMemo, type ChangeEvent } from 'react';
import _debounce from 'lodash/debounce';
import FlatInput from 'components/common/FlatInput';

type SearchInputProps = {
  collection: unknown[];
  value?: string;
  onChange: (value: string) => void;
  size?: string;
  placeholder?: string;
  className?: string;
  debounceTime?: number;
};

const SearchInput = ({
  collection,
  value,
  onChange,
  size = 'small',
  placeholder = 'search...',
  className,
  debounceTime = 200,
}: SearchInputProps) => {
  const [uncontrolledValue, setUncontrolledValue] = useState('');
  const isControlled = value !== undefined;

  const debouncedOnChange = useCallback(
    (inputValue: string) => {
      if (onChange) {
        onChange(inputValue);
      }
    },
    [onChange],
  );

  const debouncedCallback = useMemo(
    () => _debounce(debouncedOnChange, debounceTime),
    [debouncedOnChange, debounceTime],
  );

  useEffect(() => {
    return () => {
      debouncedCallback.cancel();
    };
  }, [debouncedCallback]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!isControlled) {
      setUncontrolledValue(inputValue);
    }
    debouncedCallback(inputValue);
  };

  const actualValue = isControlled ? value : uncontrolledValue;

  if (collection && collection.length > 0) {
    return (
      <FlatInput
        data-test-id="page-search-input"
        size={size}
        placeholder={placeholder}
        onChange={handleChange}
        className={className}
        value={actualValue}
      />
    );
  }

  return null;
};

export default SearchInput;
